import { useCallback, useEffect, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import { storageApi } from '@/services/storageApi';
import type { UploadResult } from '@/models/auth';

interface Props {
  onUploaded: (result: UploadResult | null) => void;
  disabled?: boolean;
}

export function ScreenshotDrop({ onUploaded, disabled }: Props) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploaded, setUploaded] = useState<UploadResult | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      if (disabled || uploading) return;
      setError('');
      setUploading(true);
      try {
        const r = await storageApi.upload(file);
        setUploaded(r);
        onUploaded(r);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Upload fehlgeschlagen');
        setUploaded(null);
        onUploaded(null);
      } finally {
        setUploading(false);
      }
    },
    [disabled, uploading, onUploaded]
  );

  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      if (disabled || uploading) return;
      const file = e.clipboardData?.files?.[0];
      if (file && file.type.startsWith('image/')) {
        e.preventDefault();
        void handleFile(file);
      }
    }
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [handleFile, disabled, uploading]);

  function clear() {
    setUploaded(null);
    onUploaded(null);
  }

  const tone = uploaded
    ? 'var(--color-success)'
    : dragOver
    ? 'var(--accent)'
    : 'var(--text-muted)';

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file && file.type.startsWith('image/')) void handleFile(file);
      }}
      className="inline-flex items-center gap-2 px-3 py-2 text-[12px] font-mono"
      style={{
        border: `1px ${dragOver || uploaded ? 'solid' : 'dashed'} ${tone}`,
        color: tone,
        background: dragOver || uploaded ? 'var(--bg-light)' : 'transparent',
      }}
    >
      <Paperclip size={13} strokeWidth={1.5} />
      {uploading && <span>uploading…</span>}
      {!uploading && uploaded && (
        <>
          <span>
            {uploaded.original_filename} (storage/{uploaded.id})
          </span>
          <button
            type="button"
            onClick={clear}
            className="text-[var(--text-muted)] hover:text-[var(--text-primary)] ml-1"
          >
            <X size={12} />
          </button>
        </>
      )}
      {!uploading && !uploaded && <span>Screenshot droppen / ⌘V einfügen</span>}
      {error && (
        <div className="text-[var(--color-error)] text-[10px] ml-2">{error}</div>
      )}
    </div>
  );
}
